import { useEffect, useRef } from 'react'

interface ConfirmModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  confirmVariant?: 'primary' | 'danger' | 'warning'
  isLoading?: boolean
}

const CONFIRM_BUTTON_VARIANTS: Record<string, string> = {
  danger:
    'bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700 text-white focus:ring-red-500',
  warning:
    'bg-amber-600 hover:bg-amber-700 dark:bg-amber-600 dark:hover:bg-amber-700 text-white focus:ring-amber-500',
  primary:
    'bg-primary-600 hover:bg-primary-700 dark:bg-primary-600 dark:hover:bg-primary-700 text-white focus:ring-primary-500',
}

function ModalActions({
  onClose,
  onConfirm,
  confirmText,
  cancelText,
  confirmVariant,
  isLoading,
}: {
  onClose: () => void
  onConfirm: () => void
  confirmText: string
  cancelText: string
  confirmVariant: string
  isLoading: boolean
}) {
  // eslint-disable-next-line security/detect-object-injection
  const variantClass = CONFIRM_BUTTON_VARIANTS[confirmVariant] ?? CONFIRM_BUTTON_VARIANTS.primary
  return (
    <div className="flex gap-3 justify-end">
      <button
        type="button"
        onClick={onClose}
        disabled={isLoading}
        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {cancelText}
      </button>
      <button
        type="button"
        onClick={onConfirm}
        disabled={isLoading}
        className={`px-4 py-2 text-sm font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${variantClass}`}
      >
        {isLoading ? 'Processing...' : confirmText}
      </button>
    </div>
  )
}

export function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmVariant = 'danger',
  isLoading = false,
}: ConfirmModalProps) {
  const modalRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoading) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose, isLoading])

  if (!isOpen) return null

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose()
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      role="button"
      tabIndex={0}
      onClick={handleBackdropClick}
      onKeyDown={(e) => {
        if (e.target === e.currentTarget && !isLoading && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          onClose()
        }
      }}
      aria-label="Close modal"
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 animate-in fade-in zoom-in duration-200"
      >
        <div className="p-6">
          <h3 id="modal-title" className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {title}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">{message}</p>

          <ModalActions
            onClose={onClose}
            onConfirm={onConfirm}
            confirmText={confirmText}
            cancelText={cancelText}
            confirmVariant={confirmVariant}
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  )
}
